import React, { useEffect, useState } from "react";
import "./MainPage.css";
import { fetchProductDetails } from "./api";
import { NOVA_GROUP_MAPPING, NUTRIENT_TRANSLATIONS, TAG_ICONS, COLORS, NutrientIcons, statusColors, statusTranslations, defaultDailyIntake, createEcoScoreDescription, POSITIVE_TRANSLATIONS, NEGATIVE_TRANSLATIONS, NUTRI_SCORE_DESCRIPTIONS } from "../constants";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";

const ScoreCircle = ({ grade, label, isActive, onToggle }) => {
    const colors = {
        A: "#28a745",
        B: "#8dc63f",
        C: "#f4c20d",
        D: "#ff8c00",
        E: "#e63946",
        unknown: "#888888",
    };

    const displayGrade = grade ? grade.replace(/\s+/g, "").toUpperCase() : "Bilinmiyor";

    return (
        <div className={`score-circle-container ${isActive ? "active" : ""}`} onClick={onToggle}>
            <div
                className={`circle ${!grade ? "bilinmiyor" : ""}`}
                style={{
                    backgroundColor: grade ? colors[grade.replace(/\s+/g, "").toUpperCase()] : colors.unknown,
                    color: "#fff",
                }}
            >
                {grade && (
                    <div className={`expand-icon ${isActive ? "expanded" : ""}`}></div>
                )}
                {displayGrade}
            </div>
            <p className="score-label">{label}</p>
        </div>
    );
};

export const Scores = ({ productDetails }) => {
    const [activeIndex, setActiveIndex] = useState(null);
    const [selectedComponent, setSelectedComponent] = useState(null);

    const scores = [
        {
            grade: productDetails?.nutri_score_grade,
            label: "Besin Kalitesi",
            description: (
                <div className="processing-info fun-card">
                    <h3>🍎 Besin Kalitesi</h3>
                    <p>
                        <strong>Besin Kalitesi Puanı</strong> ürünlerin sağlığa etkisini değerlendirir ve
                        "A"dan "E"ye kadar sıralanır. "A" en sağlıklı, "E" ise en sağlıksız seçenektir.
                    </p>
                    <p className="highlight">
                        Bu ürün, <strong>"{productDetails?.nutri_score_grade?.toUpperCase()}"</strong> puanı ile
                        değerlendirilmiştir ve {NUTRI_SCORE_DESCRIPTIONS[productDetails?.nutri_score_grade.toUpperCase()]}
                    </p>
                    <div className="score-bars">
                        <div className="bar-section negative">
                            <h4>⚠️ Negatif Bileşenler</h4>
                            {productDetails?.nutri_score_details?.components?.negative?.map((comp, idx) => (
                                <div key={idx} className="bar-item">
                                    <span>{NEGATIVE_TRANSLATIONS[comp.id]?.name || comp.id}</span>
                                    <div className="bar-container">
                                        <div
                                            className="bar negative-bar"
                                            style={{ width: `${(comp.points / comp.points_max) * 100}%` }}
                                        ></div>
                                    </div>
                                    <small>
                                        {comp.points} puan
                                    </small>
                                </div>
                            ))}
                        </div>
                        <div className="bar-section positive">
                            <h4>🌟 Pozitif Bileşenler</h4>
                            {productDetails?.nutri_score_details?.components?.positive?.map((comp, idx) => (
                                <div key={idx} className="bar-item">
                                    <span>{POSITIVE_TRANSLATIONS[comp.id]?.name || comp.id}</span>
                                    <div className="bar-container">
                                        <div
                                            className="bar positive-bar"
                                            style={{ width: `${(comp.points / comp.points_max) * 100}%` }}
                                        ></div>
                                    </div>
                                    <small>
                                        {comp.points} puan
                                    </small>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            ),
        },
        {
            grade: NOVA_GROUP_MAPPING[productDetails?.nova_group],
            label: "İşlenmişlik Düzeyi",
            description: (
                <div className="processing-info fun-card">
                    <h3>⚙️ İşlenmişlik Düzeyi</h3>
                    <p className="processing-summary">
                        <span className="info-icon">ℹ️</span>
                        Gıda ürünleri işlenmişlik düzeyine göre şu dört gruba ayrılır:
                    </p>
                    <div className="processing-levels-section">
                        <ul className="processing-levels-list">
                            <li className="level-item background-a">
                                <div className="level-content">
                                    <span className="level-icon">🍎</span>
                                    <div className="level-description">
                                        <strong>A:</strong> Az İşlenmiş veya Doğal
                                    </div>
                                </div>
                            </li>
                            <li className="level-item background-b">
                                <div className="level-content">
                                    <span className="level-icon">🥘</span>
                                    <div className="level-description">
                                        <strong>B:</strong> Minimum İşlenmiş
                                    </div>
                                </div>
                            </li>
                            <li className="level-item background-c">
                                <div className="level-content">
                                    <span className="level-icon">🍪</span>
                                    <div className="level-description">
                                        <strong>C:</strong> Orta Derecede İşlenmiş
                                    </div>
                                </div>
                            </li>
                            <li className="level-item background-e">
                                <div className="level-content">
                                    <span className="level-icon">⚠️</span>
                                    <div className="level-description">
                                        <strong>E:</strong> Yüksek Derecede İşlenmiş
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                    <div className="product-info">
                        <p>
                            <span className="info-icon" style={{ marginRight: '8px' }}>ℹ️</span>
                            Bu ürün <strong>üst düzey işlenmiş</strong> kategorisinde yer almakta ve{" "}
                            <strong>{productDetails?.nova_group_data && productDetails?.nova_group_data.length} katkı maddesi/bileşen</strong> içermektedir.
                        </p>
                    </div>
                    <div className="component-tags">
                        {productDetails?.nova_group_data?.map((item, idx) => (
                            <span
                                key={idx}
                                className={`component-tag additive`}
                                onClick={() => setSelectedComponent(idx === selectedComponent ? null : idx)}
                            >
                                {item.type === "Additives" ? item.code : item.tr_name || item.name}
                            </span>
                        ))}
                    </div>
                    {selectedComponent !== null && (
                        <div className="component-details">
                            <h4>{productDetails?.nova_group_data[selectedComponent].tr_name || productDetails?.nova_group_data[selectedComponent].name}</h4>
                            <p>{productDetails?.nova_group_data[selectedComponent].description || "Bu bileşen hakkında bilgi mevcut değil."}</p>
                            <p className="type-info">
                                Bu bir{" "}
                                <strong>
                                    {productDetails?.nova_group_data[selectedComponent].type === "Additives"
                                        ? "katkı maddesidir"
                                        : "bileşendir"}
                                </strong>
                                .
                            </p>
                        </div>
                    )}
                </div>
            ),
        },
        {
            grade: productDetails?.eco_score_grade,
            label: "Çevresel Etki",
            description: createEcoScoreDescription(productDetails?.eco_score_details),
        },
    ];

    return (
        <div className="scores-section">
            <div className="circle-row">
                {scores.map((score, index) => (
                    <ScoreCircle
                        key={index}
                        grade={score.grade}
                        label={score.label}
                        isActive={activeIndex === index}
                        onToggle={() => setActiveIndex(activeIndex === index ? null : index)}
                    />
                ))}
            </div>
            {activeIndex !== null && (
                <div className="scores-card">
                    <div className="balloon-connector"></div>
                    <div className="details-content">{scores[activeIndex].description}</div>
                </div>
            )}
        </div>
    );
};

const TagCircle = ({ icon, label, color, isPositive, isContextual, reasons, tagKey, isActive, onToggle }) => {
    const contextualLabel = isPositive ? `${label}` : `${label} Değildir`;
    const simpleLabel = isPositive ? `${label} İçerir` : `${label} İçermez`;
    const finalLabel = tagKey === "gluten"
        ? (!isPositive ? `${label} İçerir` : `${label} İçermez`)
        : (isContextual ? contextualLabel : simpleLabel);
    const hasDetails = reasons && reasons.length > 0;
    const showCross = (() => {
        if (tagKey === "gluten") {
            return isPositive;
        }
        if (isContextual) {
            return !isPositive;
        }
        return isPositive;
    })();

    return (
        <div
            className={`tag-circles ${isActive ? "active" : ""}`}
            onClick={hasDetails ? onToggle : null}
        >
            <div
                className="tag-circle"
                style={{
                    backgroundColor: color,
                    transform: isActive ? "scale(1.2)" : "scale(1)",
                    boxShadow: isActive ? "0 6px 10px rgba(0, 0, 0, 0.2)" : "none",
                    marginBottom: isActive ? "20px" : "10px",
                }}
            >
                {showCross && (
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            width: "100%",
                            height: "2px",
                            backgroundColor: "red",
                            transform: "translate(-50%, -50%) rotate(45deg)",
                            zIndex: 2,
                        }}
                    ></div>
                )}
                <img
                    src={icon}
                    alt={label}
                    className={`icon ${showCross ? "icon-crossed" : ""}`}
                    style={{ width: "40px", height: "40px" }}
                />
                {hasDetails && (
                    <span
                        className={`expand-icon ${isActive ? "expanded" : ""}`}
                        style={{
                            position: "absolute",
                            bottom: 5,
                            width: 10,
                            height: 10,
                            borderLeft: "2px solid #fff",
                            borderTop: "2px solid #fff",
                            transform: isActive ? "rotate(-135deg)" : "rotate(45deg)",
                            transition: "transform 0.3s ease",
                        }}
                    ></span>
                )}
            </div>
            <p className="tag-label" style={{ marginTop: isActive ? "10px" : "5px" }}>
                {finalLabel}
            </p>
        </div>
    );
};

export const TagsSection = ({ labels }) => {
    const [activeIndex, setActiveIndex] = useState(null);

    const tags = [
        { key: "gluten", label: "Gluten", color: "#FF6F61" },
        { key: "lactose", label: "Laktoz", color: "#FFD54F" },
        { key: "vegan", label: "Vegan", color: "#81C784" },
        { key: "vegetarian", label: "Vejetaryan", color: "#FFB74D" },
        { key: "palm_oil", label: "Palm Yağı", color: "#A1887F" },
        { key: "halal", label: "Helal", color: "#4CAF50" },
    ];

    const renderTagRow = (tagGroup, rowIndex) => (
        <>
            <div className="tag-row">
                {tagGroup?.map(({ key, label, color }, index) => {
                    const isPositive = labels && labels[key]?.status === true;
                    const reasons = labels && labels[key]?.reasons_tr || [];
                    const isActive = activeIndex === rowIndex * 10 + index;
                    const isContextual = key === "vegan" || key === "vegetarian" || key === "halal";

                    return (
                        <TagCircle
                            key={key}
                            tagKey={key}
                            icon={TAG_ICONS[key]}
                            label={label}
                            color={color}
                            isPositive={isPositive}
                            reasons={reasons.map((reason) => reason.charAt(0).toUpperCase() + reason.slice(1))}
                            isActive={isActive}
                            isContextual={isContextual}
                            onToggle={() =>
                                setActiveIndex(activeIndex === rowIndex * 10 + index ? null : rowIndex * 10 + index)
                            }
                        />
                    );
                })}
            </div>
            {tagGroup.map(({ key, label }, index) => {
                const isActive = activeIndex === rowIndex * 10 + index;
                const reasons = labels && labels[key]?.reasons_tr?.map(
                    (reason) => reason.charAt(0).toUpperCase() + reason.slice(1)
                ) || [];
                const isContextual = key === "vegan" || key === "vegetarian" || key === "halal";

                if (isActive && reasons.length > 0) {
                    return (
                        <div className="details-card tag-details full-row" key={`${key}-details`}>
                            <div className="details-content">
                                <p>
                                    <strong>
                                        Bu ürün {label}{" "}
                                        {!isContextual
                                            ? "içerir çünkü içerisinde aşağıdaki ürünler bulunur:"
                                            : "değildir çünkü içerisinde aşağıdaki ürünler bulunur:"}
                                    </strong>
                                </p>
                                <ul>
                                    {reasons.map((reason, idx) => (
                                        <li key={idx}>{reason}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    );
                }
                return null;
            })}
        </>
    );

    const firstRowTags = tags.slice(0, 3);
    const secondRowTags = tags.slice(3);

    return (
        <div className="tags-section">
            {renderTagRow(firstRowTags, 0)}
            {renderTagRow(secondRowTags, 1)}
        </div>
    );
};

export const NutrientBar = ({ nutrient, percentage, status = "unknown" }) => {
    const formattedPercentage = percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1);
    return (
        <div className="nutrient-bar">
            <p className="bar-nutrient-name">{NUTRIENT_TRANSLATIONS[nutrient] || nutrient}</p>
            <div className="bar-container">
                <div
                    className="bar"
                    style={{
                        width: `${percentage}%`,
                        backgroundColor: statusColors[status],
                    }}
                ></div>
            </div>
            <p className="bar-nutrient-value">{formattedPercentage}%</p>
            <span
                className="bar-health-indicator"
                style={{
                    color: statusColors[status],
                    fontWeight: "bold",
                }}
            >
                {statusTranslations[status] || "Bilinmiyor"}
            </span>
        </div>
    );
};

export const AllergyCard = ({ allergens }) => {
    const allergenList = typeof allergens === "string"
        ? allergens.split(",").map((allergen) => allergen.trim())
        : [];

    return (
        <div className="allergy-card-compact">
            <h3>Alerjenler</h3>
            <div className="allergen-list">
                {allergenList.length > 0 ? (
                    allergenList.map((allergen, index) => (
                        <span key={index} className="allergen-item">
                            {allergen.charAt(0).toUpperCase() + allergen.slice(1).toLowerCase()}
                        </span>
                    ))
                ) : (
                    <span className="allergen-item allergen-free">Alerjen bilgisi bulunmamaktadır.</span>
                )}
            </div>
        </div>
    );
};

export const GoodBadList = ({ badItems, positiveComponents }) => {
    const [selectedNegativeIndex, setSelectedNegativeIndex] = useState(null);
    const [selectedPositiveIndex, setSelectedPositiveIndex] = useState(null);

    const allPositiveItems = positiveComponents?.map((item) => ({
        id: item.id,
        name: POSITIVE_TRANSLATIONS[item.id]?.name || item.id,
        description: POSITIVE_TRANSLATIONS[item.id]?.description || "",
        points: item.points,
        value: item.value,
        unit: item.unit,
    }));

    return (
        <div className="good-bad-section">
            <div className="tags-row">
                <div className="tags-group">
                    <h4>Olumlu Özellikler</h4>
                    <div className="tags-container">
                        {allPositiveItems?.length > 0 ? (
                            allPositiveItems.map((item, idx) =>
                                item.points > 0 ? (
                                    <div
                                        key={idx}
                                        className="tag-container"
                                        onClick={() => {
                                            setSelectedPositiveIndex(
                                                selectedPositiveIndex === idx ? null : idx
                                            );
                                            setSelectedNegativeIndex(null);
                                        }}
                                    >
                                        <span className="tag good-tag">
                                            {item.name} ({item.value} {item.unit})
                                        </span>
                                        {selectedPositiveIndex === idx && (
                                            <div className="description-box">
                                                {item.description}
                                            </div>
                                        )}
                                    </div>
                                ) : null
                            )
                        ) : (
                            <span className="tag empty-tag">Bilgi yok</span>
                        )}
                    </div>
                </div>

                <div className="tags-group">
                    <h4>Olumsuz Özellikler</h4>
                    <div className="tags-container">
                        {badItems?.map((item, index) => (
                            <div key={index} className="tag-container">
                                <span
                                    className={`tag bad-tag ${selectedNegativeIndex === index ? "active" : ""}`}
                                    onClick={() => {
                                        setSelectedNegativeIndex(
                                            selectedNegativeIndex === index ? null : index
                                        );
                                        setSelectedPositiveIndex(null);
                                    }}
                                >
                                    {item.tr_name} {item.percentage !== 0 && `${item.percentage}%`}
                                </span>
                                {selectedNegativeIndex === index && (
                                    <div className="description-box">
                                        {item.description}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export const NutrientServing = ({ serving, dailyIntake, portion }) => {
    if (!serving || Object.keys(serving).length === 0) {
        return <p>Besin bilgisi bulunamadı.</p>;
    }

    const getStatus = (value, nutrient) => {
        const dailyValue = dailyIntake?.[nutrient] || 1;
        const percentage = (value / dailyValue) * 100;

        if (percentage > 100) return { text: "Yüksek", color: "#E57373" };
        if (percentage > 70) return { text: "Orta", color: "#FFD54F" };
        return { text: "Düşük", color: "#81C784" };
    };

    const renderRow = (name, value, unit, nutrient) => {
        const { text, color } = getStatus(value, nutrient);
        const percentage = (value / (defaultDailyIntake[nutrient] || 1)) * 100;
        const formattedPercentage = percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1);

        return (
            <div className="nutrient-row" key={name}>
                <div className="nutrient-icon-name">
                    <img src={NutrientIcons[nutrient]} alt={name} className="nutrient-icon" />
                    <span className="nutrient-name">{name}</span>
                </div>
                <div className="nutrient-value">{value}{unit}</div>
                <div className="nutrient-progress">
                    <div
                        className="progress-bar"
                        style={{
                            width: `${Math.max(5, percentage)}%`,
                            backgroundColor: color,
                        }}
                    ></div>
                </div>
                <div className="nutrient-status" style={{ color }}>
                    {text} (%{formattedPercentage} günlük ihtiyacın)
                </div>
            </div>
        );
    };

    return (
        <div className="serving-section">
            <h3 className="title">{portion ? `Porsiyon Başı (${portion}) Besin Değerleri` : `Besin Değerleri`}</h3>
            <div className="nutrient-table">
                {renderRow("Karbonhidrat", parseFloat(serving.carbohydrates || 0), "g", "carbohydrates")}
                {renderRow("Yağ", parseFloat(serving.fat || 0), "g", "fat")}
                {renderRow("Protein", parseFloat(serving.proteins || 0), "g", "proteins")}
                {renderRow("Lif", parseFloat(serving.fiber || 0), "g", "fiber")}
            </div>
        </div>
    );
};

export const IngredientsList = ({ ingredients }) => (
    <div className="ingredients-section">
        <h3 className="title">İçindekiler</h3>
        <ResponsiveContainer width="100%" height={300}>
            <PieChart>
                <Pie
                    data={ingredients?.map((item) => ({ name: formatText(item.tr), value: item.perc }))}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    label={(entry) => `${formatText(entry?.name)} (%${entry?.value})`}
                >
                    {ingredients?.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                </Pie>
                <Tooltip />
            </PieChart>
        </ResponsiveContainer>
    </div>
);

const formatText = (text) => text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();

const mockProduct = {
    "id": 5331,
    "name": "Ülker Saklıköy Sütlü Kremalı Bisküvi (100 g)",
    "barcode": "8690504082651",
    "image_url": "https://www.expayglobal.com/images/products/ulker-saklikoy-creamy-milk-100-gr.jpg",
    "category": "Atıştırmalık",
    "sub_category": "Bisküvi",
    "product_details": [
        {
            "quantity": "100 g",
            "serving_quantity": "30 g",
            "nova_group": "Üst düzey işlemden geçmiş yiyecekler",
            "nova_additives": [
                {
                    "tr_name": "E322 - Lesitin",
                    "percentage": 0.5,
                    "description": "Emülgatör olarak kullanılır, genellikle soya veya ayçiçekten elde edilir."
                },
                {
                    "tr_name": "E500 - Sodyum karbonatlar",
                    "percentage": 0.8,
                    "description": "Kabartma tozu olarak kullanılır, asidik bileşenlerle reaksiyona girerek hamuru kabartır."
                },
                {
                    "tr_name": "E202 - Potasyum sorbat",
                    "percentage": 0.2,
                    "description": "Koruyucu madde olarak kullanılır, küf ve maya oluşumunu önler."
                }
            ],
            "allergen_alerts": "Glüten, Süt, Buğday kepeği, Peyniraltı suyu tozu, Soya fasulyesi, Tam buğday unu, Yağsız süttozu, Yulaf ezmesi, Yulaf unu",
            "labels_tags": {
                "halal": { "status": true },
                "gluten": { "status": false },
                "lactose": { "status": true, "reasons": ["Milk"], "reasons_tr": ["Süt"] },
                "vegan": { "status": false, "reasons": ["Milk"], "reasons_tr": ["Süt"] },
                "vegetarian": { "status": true, "reasons": [], "reasons_tr": [] },
                "palm_oil": { "status": true, "reasons": ["Palm oil and fat"], "reasons_tr": ["Palmiye yağı ve katı yağ"] }
            },
            "nutri_score_grade": "e",
            "nutrient_levels": [
                { "name": "Fat", "status": "high", "perc": "22" },
                { "name": "Salt", "status": "moderate", "perc": "0.9" },
                { "name": "Saturated fat", "status": "high", "perc": "11" },
                { "name": "Sugars", "status": "high", "perc": "29" }
            ],
            "nutrient_info": {
                "full": { "carbohydrates": "62.0g", "fat": "22.0g", "proteins": "6.9g", "fiber": "4.7g" },
                "serving": { "carbohydrates": "18.6g", "fat": "6.6g", "proteins": "2.07g", "fiber": "1.41g" }
            },
            "ingredients_summary": [
                { "name": "Şeker", "perc": 53.12, "tr": "Şeker" },
                { "name": "palm yağı", "perc": 23.44, "tr": "palm yağı" },
                { "name": "yulaf ezmesi", "perc": 13, "tr": "yulaf ezmesi" },
                { "name": "buğday unu", "perc": 11.72, "tr": "buğday unu" },
                { "name": "tam buğday unu", "perc": 7, "tr": "tam buğday unu" }
            ],
            "nutri_score_details": {
                "score": 26,
                "negative_points": 28,
                "positive_points": 2,
                "components": {
                    "negative": [
                        { "id": "energy", "points": 6, "unit": "kJ", "value": 2029 },
                        { "id": "sugars", "points": 8, "unit": "g", "value": 29 },
                        { "id": "saturated_fat", "points": 10, "unit": "g", "value": 11 },
                        { "id": "salt", "points": 4, "unit": "g", "value": 0.9 }
                    ],
                    "positive": [
                        { "id": "fiber", "points": 2, "unit": "g", "value": 4.7 },
                        { "id": "fruits_vegetables_legumes", "points": 0, "unit": "%", "value": 0 }
                    ]
                }
            },
            "eco_score_grade": "D",
            "eco_score_score": 32,
            "eco_score_details": {
                "co2_total": 1.2,
                "co2_impact_level": "Yüksek Karbon Ayak İzi",
                "packaging": ["non_recyclable", "plastic"],
                "threatened_species_impact": "Ormansızlaşma riski içeriyor"
            }
        }
    ]
};

const MainPage = () => {
    const [product, setProduct] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            setProduct(mockProduct);
        };
        fetchData();
    }, []);

    return (
        <div className="main-page">
            <div className="content">
                <div className="product-header">
                    <img src={product?.image_url} alt={product?.name} />
                    <h3>{product?.name}</h3>
                </div>

                <Scores productDetails={product?.product_details && product?.product_details[0]} />

                <GoodBadList
                    badItems={product?.product_details && product?.product_details[0].nova_additives}
                    positiveComponents={product?.product_details && product?.product_details[0].nutri_score_details.components.positive}
                />

                <div>
                    <h3 className="title">Ürün Özellikleri</h3>
                    <TagsSection labels={product?.product_details && product?.product_details[0].labels_tags} />
                </div>

                <AllergyCard allergens={product?.product_details && product?.product_details[0].allergen_alerts} />

                <NutrientServing serving={product?.product_details && product?.product_details[0].nutrient_info.serving} portion={product?.product_details && product?.product_details[0].serving_quantity} />

                <IngredientsList ingredients={product?.product_details && product?.product_details[0].ingredients_summary} />

                <div className="nutrient-section">
                    <h3 className="title">Besin Seviyeleri</h3>
                    {product?.product_details && product?.product_details[0].nutrient_levels &&
                        product?.product_details[0].nutrient_levels
                            .filter((nutrient) => nutrient.perc && nutrient.name)
                            .sort((a, b) => parseFloat(b.perc) - parseFloat(a.perc))
                            .map((nutrient, index) => (
                                <NutrientBar
                                    key={index}
                                    nutrient={nutrient.name}
                                    percentage={parseFloat(nutrient.perc)}
                                    status={nutrient.status || "unknown"}
                                />
                            ))}
                </div>

            </div>
        </div>
    );
};

export default MainPage;
